// Ask across meetings at /o/:slug/ask (F11). POST /orgs/{id}/ask answers from the meetings the
// asker can open; every sentence carries numbered source chips (DS Timestamp, FR-M7) that open
// the player at that moment (/g/:id?t=<seconds>), and a source list sits in the 340px panel like
// the Notes screen. The session is in-memory only: nothing is stored server-side (privacy), and
// "Start over" clears it.
import { useRef, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { Badge, Button, Callout, Card, Icon, Input, Switch, Timestamp } from "@grackle/ui";
import { ApiFail } from "../api/client";
import type { AskOut, AskSourceOut } from "../api/types";
import { AppShell } from "../components/AppShell";
import { EmptyState, PageLoader } from "../components/ui";
import { adminAiPath } from "../library/routes";
import { useSession } from "../session";
import { clock } from "../watch/transcript";
import { askMeetings } from "./api";
import "./ask.css";

export const ASK_NOT_FOUND = "I couldn't find that in meetings you can access.";

type TurnState =
  | { status: "loading" }
  | { status: "done"; out: AskOut }
  | { status: "error"; code: string; message: string };

interface Turn {
  id: number;
  question: string;
  state: TurnState;
}

/** Why the page-level empty state is shown instead of the composer. */
type Blocked = { kind: "unavailable"; reason: string } | { kind: "disabled" };

function sourceKey(s: AskSourceOut): string {
  return `${s.kind}:${s.grackle_id}:${s.start_ms}:${s.end_ms}`;
}

function seconds(s: AskSourceOut): number {
  return Math.floor(s.start_ms / 1000);
}

/** Distinct sources across the answer, numbered in order of first citation. */
export function numberSources(out: AskOut): { list: AskSourceOut[]; index: Map<string, number> } {
  const list: AskSourceOut[] = [];
  const index = new Map<string, number>();
  for (const sentence of out.answer) {
    for (const src of sentence.sources) {
      const key = sourceKey(src);
      if (!index.has(key)) {
        index.set(key, list.length + 1);
        list.push(src);
      }
    }
  }
  return { list, index };
}

function providerLine(out: AskOut): string {
  const whose = out.provenance.scope === "personal" ? "your own AI account" : "your organization's AI account";
  return `Answered by ${out.provenance.model} via ${whose}. Only from meetings you can access.`;
}

export default function AskPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user, orgs, loading: sessionLoading } = useSession();
  const org = orgs.find((o) => o.slug === slug);

  const [draft, setDraft] = useState("");
  const [includeAll, setIncludeAll] = useState(false);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [blocked, setBlocked] = useState<Blocked | null>(null);
  const nextId = useRef(1);
  const inputRef = useRef<HTMLInputElement>(null);

  if (sessionLoading) return <PageLoader label="Loading…" />;
  if (!user) return <Navigate to="/signin" replace />;
  if (!org) return <Navigate to="/orgs" replace />;

  const isAdmin = org.role === "owner" || org.role === "admin";
  const busy = turns.some((t) => t.state.status === "loading");
  const latestDone = [...turns].reverse().find((t) => t.state.status === "done");
  const panelOut = latestDone && latestDone.state.status === "done" ? latestDone.state.out : null;
  const panel = panelOut ? numberSources(panelOut) : null;

  function open(src: AskSourceOut) {
    navigate(`/g/${src.grackle_id}?t=${seconds(src)}`);
  }

  async function submit() {
    const question = draft.trim();
    if (!question || busy || !org) return;
    const id = nextId.current++;
    setDraft("");
    setTurns((prev) => [...prev, { id, question, state: { status: "loading" } }]);
    const settle = (state: TurnState) =>
      setTurns((prev) => prev.map((t) => (t.id === id ? { ...t, state } : t)));
    try {
      const out = await askMeetings(org.id, question, includeAll ? { include_all_recordings: true } : undefined);
      settle({ status: "done", out });
    } catch (err) {
      if (err instanceof ApiFail && err.code === "ask_unavailable") {
        setTurns((prev) => prev.filter((t) => t.id !== id));
        setBlocked({ kind: "unavailable", reason: String(err.data?.reason ?? "no language model is connected") });
      } else if (err instanceof ApiFail && err.code === "ask_disabled") {
        setTurns((prev) => prev.filter((t) => t.id !== id));
        setBlocked({ kind: "disabled" });
      } else if (err instanceof ApiFail) {
        settle({ status: "error", code: err.code, message: err.message });
      } else {
        settle({ status: "error", code: "network", message: "Couldn't reach Grackle." });
      }
    }
  }

  function reset() {
    setTurns([]);
    setDraft("");
    setBlocked(null);
    inputRef.current?.focus();
  }

  function renderTurn(turn: Turn) {
    const { state } = turn;
    if (state.status === "loading") {
      return (
        <div className="ask__answer" role="status" aria-live="polite">
          <span className="gk-sm">Searching your meetings…</span>
        </div>
      );
    }
    if (state.status === "error") {
      const limited = state.code === "rate_limited";
      return (
        <Callout tone={limited ? "now" : "danger"} icon={limited ? "info" : "warning-circle"}>
          {limited
            ? `You've reached the limit on questions for now. ${state.message}.`
            : "Couldn't get an answer. Try again."}
        </Callout>
      );
    }
    const { out } = state;
    if (out.status === "not_found" || out.answer.length === 0) {
      return (
        <div className="ask__answer">
          <p className="gk-body ask__notfound">{out.message ?? ASK_NOT_FOUND}</p>
          <div className="gk-caption ask__provenance">{providerLine(out)}</div>
        </div>
      );
    }
    const { index } = numberSources(out);
    return (
      <div className="ask__answer">
        <p className="gk-body ask__text">
          {out.answer.map((sentence, i) => (
            <span key={i} className="ask__sentence">
              {sentence.text}{" "}
              {sentence.sources.map((src) => {
                const n = index.get(sourceKey(src)) ?? 0;
                return (
                  <Timestamp
                    key={sourceKey(src)}
                    time={`${n} · ${clock(seconds(src))}`}
                    title={`${src.title} at ${clock(seconds(src))}`}
                    aria-label={`Source ${n}: ${src.title} at ${clock(seconds(src))}`}
                    onClick={() => open(src)}
                  />
                );
              })}{" "}
            </span>
          ))}
        </p>
        <div className="gk-caption ask__provenance">{providerLine(out)}</div>
      </div>
    );
  }

  return (
    <AppShell org={org} user={user} section="ask" canRecord={user.email_verified}>
      <div className="ask">
        <div className="ask__main">
          <div className="page-head">
            <div className="page-head__grow">
              <h1>Ask Grackle</h1>
              <p>Questions answered from the meetings you can access. Every answer shows its sources.</p>
            </div>
            <Button variant="secondary" icon="arrow-counter-clockwise" onClick={reset} disabled={turns.length === 0 && !blocked}>
              Start over
            </Button>
          </div>

          {blocked ? (
            <EmptyState
              className="ask__empty"
              icon="sparkle"
              title={blocked.kind === "disabled" ? "Ask is turned off" : "Ask needs a language model"}
              body={
                blocked.kind === "disabled" ? (
                  <>Ask across meetings is turned off for this organization.</>
                ) : (
                  <>
                    Unavailable: {blocked.reason}.{" "}
                    {isAdmin ? (
                      <>
                        Connect one in <Link to={adminAiPath(org.slug)}>Settings → AI</Link>.
                      </>
                    ) : (
                      <>An org admin can connect one in Settings → AI.</>
                    )}
                  </>
                )
              }
            />
          ) : (
            <>
              <div className="ask__thread">
                {turns.length === 0 ? (
                  <EmptyState
                    className="ask__empty"
                    icon="chats"
                    title="Ask about your meetings"
                    body="Try “What did we decide about pricing?” Answers only use meetings you can open."
                  />
                ) : (
                  turns.map((turn) => (
                    <div key={turn.id} className="ask__turn">
                      <div className="ask__question">
                        <Badge>You</Badge> <span className="gk-body">{turn.question}</span>
                      </div>
                      {renderTurn(turn)}
                    </div>
                  ))
                )}
              </div>

              <div className="ask__composer">
                <Input
                  ref={inputRef}
                  variant="accent"
                  icon="sparkle"
                  aria-label="Your question"
                  placeholder="Ask across your meetings…"
                  value={draft}
                  maxLength={1000}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      void submit();
                    }
                  }}
                  onSend={() => void submit()}
                />
                <label className="ask__toggle gk-sm">
                  <Switch size="sm" label="Include all recordings" checked={includeAll} onChange={setIncludeAll} />
                  Include all recordings (not just meetings)
                </label>
              </div>
            </>
          )}
        </div>

        <aside className="ask__source" aria-label="Sources">
          <div className="ask__source-head">
            <Icon name="file-text" size={18} />
            <span className="ask__source-title">Sources</span>
          </div>
          {panel && panel.list.length > 0 ? (
            <ol className="ask__source-list">
              {panel.list.map((src, i) => (
                <li key={sourceKey(src)} className="ask__source-item">
                  <Card size="sm">
                    <div className="ask__source-row">
                      <span className="mono ask__source-n">{i + 1}</span>
                      <Link to={`/g/${src.grackle_id}?t=${seconds(src)}`} className="ask__source-link">
                        {src.title || "Untitled"}
                      </Link>
                      {src.kind === "note" ? <Badge>Notes</Badge> : null}
                      <Timestamp time={clock(seconds(src))} onClick={() => open(src)} />
                    </div>
                    <p className="gk-sm ask__snippet">{src.snippet}</p>
                  </Card>
                </li>
              ))}
            </ol>
          ) : (
            <p className="gk-sm ask__source-empty">Sources for the latest answer appear here.</p>
          )}
        </aside>
      </div>
    </AppShell>
  );
}
